import axios, { AxiosResponse, Method } from "axios";

import { getToken, getRememberMe, setToken, removeUserSession } from "./auth";
import { IAntDProTableRequest } from "../general/type/general.types";

const apiHost = process.env.REACT_APP_API_HOST;

// Create and configure axios instance
const createAxiosInstance = () => {
  const instance = axios.create();

  // Request interceptor
  instance.interceptors.request.use(
    (config) => {
      const tokens = getToken();
      if (tokens) {
        config.headers.Authorization = `Bearer ${tokens.access.token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const rememberMe = getRememberMe();
      const tokens = getToken();

      if (error.response?.status === 401 && !originalRequest._retry && tokens) {
        originalRequest._retry = true;
        const newTokens = await refreshToken(instance);

        if (newTokens) {
          const daysToRemember = rememberMe === "true" ? 7 : 1;
          setToken(newTokens, daysToRemember);
          instance.defaults.headers.common.Authorization = `Bearer ${newTokens.access.token}`;
          return instance(originalRequest);
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

// Token refresh function
const refreshToken = async (axiosInstance: ReturnType<typeof axios.create>) => {
  const tokens = getToken();
  const rememberMe = getRememberMe();

  try {
    const resp = await axiosInstance.post("v1/auth/refresh-tokens", {
      refreshToken: tokens?.refresh.token,
      rememberMe,
    });
    return resp.data;
  } catch (e) {
    console.error("Token refresh failed:", e);
    removeUserSession();
    window.location.href = "/";
  }
};

// Initialize axios instance
const axiosInstance = createAxiosInstance();

// Add this function inside api.ts
const formatTableRequest = (tableRequest: IAntDProTableRequest) => {
  const { params, filter, sort } = tableRequest;
  const { current, pageSize, ...searchParams } = params;

  const sortParams = Object.entries(sort).map(
    ([key, order]) => `${key}:${order === "ascend" ? "asc" : "desc"}`
  );

  const sortBy = sortParams.length > 0 ? sortParams[0] : "createdAt:desc";

  return {
    ...searchParams,
    ...filter,
    limit: pageSize,
    page: current,
    sortBy,
  };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
async function apiRequest<T>(
  method: Method,
  url: string,
  data: any = {},
  isFormData: boolean = false
) {
  const token = getToken();

  // Handle table request formatting
  let params = data.params;
  if (params && "current" in params && "filter" in data) {
    params = formatTableRequest(data);
  }

  try {
    const headers: Record<string, string> = {
      Authorization: token ? `Bearer ${token.access.token}` : "",
    };

    // Don't set Content-Type for FormData to let the browser set it with boundary
    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    }

    const response: AxiosResponse = await axiosInstance({
      method,
      headers,
      url: apiHost + url,
      params,
      ...(method !== "GET" && { data }),
    });
    return { status: "success", data: response.data };
  } catch (error: any) {
    console.error("API request failed:", error);
    throw new Error(
      error.response?.data?.message || "API request failed: Connection Error"
    );
  }
}

// Function to handle binary file downloads
async function downloadFile(url: string): Promise<Blob> {
  const token = getToken();

  try {
    const response = await axiosInstance({
      method: "GET",
      headers: { Authorization: token ? `Bearer ${token.access.token}` : "" },
      url: apiHost + url,
      responseType: "blob",
    });

    return response.data;
  } catch (error: any) {
    console.error("File download failed:", error);
    throw new Error(
      error.response?.data?.message || "File download failed: Connection Error"
    );
  }
}

export { apiRequest, downloadFile };
